import { Navigate, Route, Routes } from "react-router-dom";

import { PreSessionLoading, SCREEN_ELEMENTS } from "./screens/elements";
import { homePathFor } from "./session/access";
import { useSession } from "./session/SessionProvider";
import { ScreenSwitch } from "./shell/Controls";
import { Protected } from "./shell/Protected";
import { SURFACE_PREFIX, type Surface } from "./system/navigation";
import { SCREENS } from "./system/screens";

// ---------------------------------------------------------------------------
// The router. Three surfaces behind a session guard, the public screens in
// front of it, and no third way in.
//
// THE ROUTES COME FROM system/screens.ts, WHICH IS A LIST RATHER THAN THIS FILE.
// A route table nobody can enumerate cannot be checked against the demo gallery
// registry, and definition-of-done point 9 asks for exactly that comparison.
// The elements live in screens/elements.tsx keyed by the same ids, and
// TypeScript makes that map exhaustive both ways.
//
// THAT PREVIOUS PARAGRAPH USED TO BE THE ONLY THING SAYING SO, AND A COMMENT IS
// NOT A CHECK. Every route assertion in the suite — verify-V4.sh's emailed-link
// paths, verify-V6.sh's nav destinations — reads system/screens.ts. If this
// file were reverted to inline <Route path="clients/new"> JSX, that table would
// become a dead manifest and all of them would stay green over it. It is the
// same shape as the surfaces.css defect, where a perfect palette was measured
// 178 times and never imported.
//
// scripts/lib/route-wiring.mjs closes it, and not by reading this file: it
// bundles App.tsx with system/screens REPLACED BY A SYNTHETIC TABLE, calls the
// functions below, and reports the paths they actually produce. Inline JSX
// produces the real paths under a synthetic table, which is the failure. Both
// `App` and `SurfaceRoutes` must therefore stay callable without a Router
// context or a DOM — which is why the surface arrives as a prop below rather
// than being read back out of the URL.
//
// THE PUBLIC ROUTES ARE PUBLIC ON PURPOSE, AND THERE ARE SEVEN: sign-in,
// sign-up, choose-organisation, and the four screens an emailed link lands on.
// Each of the latter carries a single-use token that the DATABASE validates;
// none can be reached with a guess, and none shows anything until the server
// has agreed. Everything else is inside Protected.
//
// `/` does not redirect to the console. A worker who taps the bare domain must
// arrive at the worker app, and sending everyone to /console first would show
// them a redirect through a surface they are not entitled to — which looks
// exactly like being refused.
// ---------------------------------------------------------------------------

export function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />

      {SCREENS.filter((screen) => screen.home === "public").map((screen) => (
        <Route key={screen.id} path={screen.path} element={SCREEN_ELEMENTS[screen.id]} />
      ))}

      <Route
        path={`${SURFACE_PREFIX.console}/*`}
        element={
          <Protected surface="console">
            <SurfaceRoutes surface="console" />
          </Protected>
        }
      />
      <Route
        path={`${SURFACE_PREFIX.portal}/*`}
        element={
          <Protected surface="portal">
            <SurfaceRoutes surface="portal" />
          </Protected>
        }
      />
      <Route
        path={`${SURFACE_PREFIX.worker}/*`}
        element={
          <Protected surface="worker">
            <SurfaceRoutes surface="worker" />
          </Protected>
        }
      />
    </Routes>
  );
}

/**
 * Inside a surface. Anything under the prefix with no screen behind it is a
 * genuine 404 rather than a blank shell — a shell with nothing in it reads as a
 * loading state that never finishes.
 *
 * THE SURFACE ARRIVES AS A PROP AND IS NOT READ BACK OUT OF THE URL. It used to
 * call useLocation() and re-derive it with surfaceForPath, which was a third
 * spelling of a fact the parent <Route> and the <Protected> beside it already
 * state — and, being a hook, it made this function unreachable to any check
 * that does not stand up a Router. The connection assertion has to CALL this to
 * see what it produces, so the hook was the cost of the redundancy.
 *
 * EXPORTED for the same reason: scripts/lib/route-wiring.mjs reaches it through
 * App()'s own element tree rather than importing it by name, so an App that
 * stopped rendering it would be caught, but the export is what lets the walk
 * confirm the type it found is this one.
 *
 * THE CONSOLE'S OWN ROUTES ARE NOT PERMISSION-GATED HERE. Hiding a link is not
 * authorisation, and neither is refusing to route: the API refuses the request
 * and the database refuses the row. Someone who types /console/clients without
 * client.read reaches a screen that says the server would not load it, which is
 * true, rather than a 404 that says the feature does not exist, which is not.
 */
export function SurfaceRoutes({ surface }: { surface: Surface }) {
  return <Routes>{routesFor(surface)}</Routes>;
}

/**
 * The <Route> elements a surface owns, from the table.
 *
 * ORDERING IS PRESERVED FROM system/screens.ts AND IT MATTERS. `workers/new`
 * must come before `workers/:id` or the literal "new" is captured as an id and
 * the create screen becomes a 404 — the API has the same hazard with
 * /v1/workers/me. React Router ranks routes rather than taking the first match,
 * so this is belt and braces; the table is still written in the order that
 * would be correct under a first-match router, because that is the assumption
 * every reader arrives with.
 */
export function routesFor(surface: Surface) {
  return SCREENS.filter((screen) => screen.home === surface).map((screen) =>
    screen.path === "" ? (
      <Route key={screen.id} index element={<ScreenSwitch id={screen.id}>{SCREEN_ELEMENTS[screen.id]}</ScreenSwitch>} />
    ) : (
      <Route key={screen.id} path={screen.path} element={<ScreenSwitch id={screen.id}>{SCREEN_ELEMENTS[screen.id]}</ScreenSwitch>} />
    ),
  );
}

function RootRedirect() {
  const { state, me } = useSession();
  if (state === "loading") return <PreSessionLoading />;
  if (!me?.authenticated) return <Navigate to="/sign-in" replace />;
  const home = homePathFor(me);
  if (home) return <Navigate to={home} replace />;
  // Signed in, entitled to no surface. Protected says the same thing for the
  // same reason; going there keeps one wording of it.
  return <Navigate to={SURFACE_PREFIX.console} replace />;
}
